import React, { useState, useEffect, useCallback } from 'react';
import challengeApi from '../challengeApi';

export default function Challenge({ onChange, error, disabled }) {
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchChallenge = useCallback(async () => {
    setLoading(true);
    setAnswer('');
    setLoadError('');
    // Do NOT call onChange here — calling it would trigger the parent's handler
    // which clears challengeError, hiding the alert before the user sees it.
    // The parent gets the updated challenge_id once the user types their next answer.
    try {
      const { data } = await challengeApi.get('/challenge');
      setChallenge(data);
    } catch {
      setLoadError('Could not load security check. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);

  // When parent signals a wrong-answer error, auto-fetch a fresh question
  useEffect(() => {
    if (error) fetchChallenge();
  }, [error, fetchChallenge]);

  const handleChange = (e) => {
    const val = e.target.value;
    setAnswer(val);
    if (challenge) {
      onChange({ challenge_id: challenge.challenge_id, answer: val });
    }
  };

  const displayError = error || loadError;

  return (
    <div className={`p-4 rounded-xl border transition-colors ${
      displayError ? 'bg-red-950/20 border-red-800/50' : 'bg-lx-card border-lx-border'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Security Check</span>
        <div className="h-px flex-1 bg-lx-border" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 h-10">
          <div className="w-3.5 h-3.5 border border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500 text-sm">Loading…</span>
        </div>
      ) : loadError ? (
        <div className="flex items-center justify-between">
          <span className="text-red-400 text-sm">{loadError}</span>
          <button type="button" onClick={fetchChallenge}
            className="text-indigo-400 hover:text-indigo-300 text-sm underline">
            Retry
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-gray-300 font-mono text-sm bg-lx-bg px-3 py-2 rounded-lg border border-lx-border whitespace-nowrap">
            {challenge?.question}
          </span>
          <span className="text-gray-500 text-sm">=</span>
          <input
            type="number"
            value={answer}
            onChange={handleChange}
            placeholder="?"
            disabled={disabled}
            className={`w-20 px-3 py-2 bg-lx-bg border rounded-lg text-white text-center font-mono
              text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
              disabled:opacity-50 transition-colors
              ${displayError ? 'border-red-600' : 'border-lx-border'}`}
          />
          {displayError && (
            <button type="button" onClick={fetchChallenge}
              className="text-xs text-indigo-400 hover:text-indigo-300 underline whitespace-nowrap">
              New question
            </button>
          )}
        </div>
      )}

      {displayError && (
        <p className="text-red-400 text-xs mt-2">{displayError}</p>
      )}
    </div>
  );
}
